import React, { memo } from "react";
import { EdgeProps, EdgeLabelRenderer, useReactFlow } from "reactflow";
import { motion } from "framer-motion";
import styled from "@emotion/styled";
import { useDiagramStore } from "../../store/diagramStore";
import { useTheme } from "../../context/ThemeContext";

const EdgePath = styled(motion.path)<{
  $isHighlighted: boolean;
  $type?: string;
  $isDarkMode: boolean;
}>`
  stroke: ${(props) => {
    if (props.$isDarkMode) {
      if (props.$isHighlighted) return "#a855f7";
      switch (props.$type) {
        case "convoy":
          return "#f59e0b";
        case "convoy-route":
          return "#10b981";
        case "webhook":
          return "#06b6d4";
        default:
          return "#9ca3af";
      }
    } else {
      if (props.$isHighlighted) return "#5e72e4";
      switch (props.$type) {
        case "convoy":
          return "#fb6340";
        case "convoy-route":
          return "#10b981";
        case "webhook":
          return "#11cdef";
        default:
          return "#172b4d";
      }
    }
  }};
  stroke-width: ${(props) => (props.$isHighlighted ? 3 : 2)};
  fill: none;
  filter: ${(props) => (props.$isHighlighted ? `drop-shadow(0 0 8px ${props.$isDarkMode ? "rgba(168, 85, 247, 0.6)" : "rgba(102, 126, 234, 0.6)"})` : "none")};
  transition: all 0.3s ease;
  opacity: ${(props) => (props.$isHighlighted ? 1 : 0.7)};

  ${(props) =>
    props.$isHighlighted &&
    `
    animation: pulseGlow 2s ease-in-out infinite;

    @keyframes pulseGlow {
      0%, 100% {
        filter: drop-shadow(0 0 4px ${props.$isDarkMode ? "rgba(168, 85, 247, 0.6)" : "rgba(102, 126, 234, 0.6)"});
      }
      50% {
        filter: drop-shadow(0 0 12px ${props.$isDarkMode ? "rgba(168, 85, 247, 0.8)" : "rgba(102, 126, 234, 0.8)"});
      }
    }
  `}
`;

const EdgeLabel = styled.div<{ $x: number; $y: number }>`
  position: absolute;
  transform: translate(-50%, -50%) translate(${(props) => props.$x}px, ${(props) => props.$y}px);
  background: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  pointer-events: all;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translate(-50%, -50%) translate(${(props) => props.$x}px, ${(props) => props.$y}px) scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const AnimatedDot = styled(motion.circle)`
  fill: white;
  filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.3));
`;

interface CustomEdgeData {
  label?: string;
  type?: "direct" | "convoy" | "convoy-route" | "webhook";
  animated?: boolean;
  convoyRouteId?: string;
  curveOffset?: number;
}

export const AnimatedEdge: React.FC<EdgeProps<CustomEdgeData>> = memo(({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, selected, source, target }) => {
  const { highlightedPath, selectedConnectionId, updateConnection } = useDiagramStore();
  const { isDarkMode } = useTheme();
  const { setEdges } = useReactFlow();

  const isHighlighted = Boolean((highlightedPath.includes(source) && highlightedPath.includes(target)) || selectedConnectionId === id || selected);

  // Compute cubic path with a movable midpoint offset
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;
  const length = Math.hypot(dx, dy) || 1;
  // normal vector (perpendicular) unit
  const nx = -dy / length;
  const ny = dx / length;
  const offset = data?.curveOffset ?? 0;
  const cx = midX + nx * offset;
  const cy = midY + ny * offset;

  // Control points: pull near midpoint towards (cx, cy)
  const c1x = (sourceX + cx) / 2;
  const c1y = (sourceY + cy) / 2;
  const c2x = (targetX + cx) / 2;
  const c2y = (targetY + cy) / 2;

  const edgePath = `M ${sourceX},${sourceY} C ${c1x},${c1y} ${c2x},${c2y} ${targetX},${targetY}`;

  // Compute a point on the curve at t=0.5 so the control dot sits on the path
  const t = 0.5;
  const oneMinusT = 1 - t;
  const labelX = oneMinusT * oneMinusT * oneMinusT * sourceX + 3 * oneMinusT * oneMinusT * t * c1x + 3 * oneMinusT * t * t * c2x + t * t * t * targetX;
  const labelY = oneMinusT * oneMinusT * oneMinusT * sourceY + 3 * oneMinusT * oneMinusT * t * c1y + 3 * oneMinusT * t * t * c2y + t * t * t * targetY;

  const markerEnd = data?.type === "convoy" ? "url(#convoy-arrow)" : "url(#arrow)";

  return (
    <>
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={isDarkMode ? "#9ca3af" : "#172b4d"} />
        </marker>
        <marker id="convoy-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={isDarkMode ? "#f59e0b" : "#fb6340"} />
        </marker>
      </defs>

      <EdgePath
        d={edgePath}
        $isHighlighted={isHighlighted}
        $type={data?.type}
        $isDarkMode={isDarkMode}
        markerEnd={markerEnd}
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{
          pathLength: 1,
          opacity: 1,
          strokeDasharray: isHighlighted
            ? "15 5" // Animated dashes for highlighted paths
            : data?.type === "convoy" || data?.type === "convoy-route"
            ? "10 5"
            : "none",
          strokeDashoffset: isHighlighted ? [0, -20] : 0, // Animate dash movement for flow direction
        }}
        transition={{
          duration: isHighlighted ? 1 : 0.5,
          strokeDashoffset: isHighlighted
            ? {
                duration: 1,
                repeat: Infinity,
                ease: "linear",
              }
            : undefined,
        }}
      />

      {(data?.animated || isHighlighted) && (
        <AnimatedDot
          r="4"
          initial={{ offsetDistance: "0%" }}
          animate={{ offsetDistance: "100%" }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear",
          }}
        >
          <animateMotion dur="2s" repeatCount="indefinite">
            <mpath href={`#${id}`} />
          </animateMotion>
        </AnimatedDot>
      )}

      <path id={id} d={edgePath} fill="none" stroke="none" />

      {true && (
        <EdgeLabelRenderer>
          <EdgeLabel $x={labelX} $y={labelY}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {data?.label}
              <div
                title="Drag to adjust curve"
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 12,
                  background: "transparent",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "grab",
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const startY = e.clientY;
                  const startOffset = data?.curveOffset ?? 0;

                  const onMove = (ev: MouseEvent) => {
                    const dy = ev.clientY - startY;
                    const newOffset = Math.max(-300, Math.min(300, startOffset + dy));
                    setEdges((eds) => eds.map((edge) => (edge.id === id ? { ...edge, data: { ...edge.data, curveOffset: newOffset } } : edge)));
                    // Persist in store so it survives refresh
                    updateConnection(id, { curveOffset: newOffset });
                  };
                  const onUp = () => {
                    window.removeEventListener("mousemove", onMove);
                    window.removeEventListener("mouseup", onUp);
                  };
                  window.addEventListener("mousemove", onMove);
                  window.addEventListener("mouseup", onUp);
                }}
              >
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "#5e72e4",
                    border: "2px solid white",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>
          </EdgeLabel>
        </EdgeLabelRenderer>
      )}
    </>
  );
});
